import { useEffect } from "react";
import { Link, useRouter, type ErrorComponentProps } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import { isChunkLoadError, reloadOnceForChunkError } from "@/lib/chunk-reload";
import { useTranslation } from "@/i18n";

const isDev = import.meta.env.DEV;

/**
 * Friendly, generic error boundary suitable for any route. Never surfaces
 * raw database, Supabase, or stack details to end users.
 */
export function RouteErrorBoundary({ error, reset }: ErrorComponentProps) {
  const router = useRouter();
  const { t } = useTranslation("errors");

  const staleCode = isChunkLoadError(error);
  useEffect(() => {
    if (isDev) {
      // Dev-only logging so production users never see stack details in the console.
      console.error("[route error]", error);
    }
    reportLovableError(error, { boundary: "route_error_component", stale_code: staleCode });
    if (staleCode) reloadOnceForChunkError();
  }, [error, staleCode]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-3xl text-gold-gradient">
          {t("routeErrorTitle", "Something went wrong")}
        </h1>
        <p className="mt-3 text-sm text-muted-foreground">
          {t(
            "routeErrorBody",
            "We couldn't load this page right now. Please try again — if the problem persists you can head back or return home.",
          )}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <Button
            className="bg-gold-gradient text-gold-foreground hover:opacity-90"
            onClick={() => {
              if (staleCode) {
                window.location.reload();
                return;
              }
              router.invalidate();
              reset();
            }}
          >
            {t("retry", "Retry")}
          </Button>
          <Button variant="outline" onClick={() => router.history.back()}>
            {t("back", "Back")}
          </Button>
          <Button variant="ghost" asChild>
            <Link to="/">{t("goHome", "Go home")}</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Friendly 404 boundary. Used for both unmatched URLs and `notFound()` throws.
 */
export function RouteNotFoundBoundary() {
  const { t } = useTranslation("errors");
  return (
    <div className="flex min-h-[60vh] items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="font-display text-6xl text-gold-gradient">{t("notFound404", "404")}</h1>
        <h2 className="mt-4 text-xl font-semibold">{t("notFoundHeading", "Page not found")}</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          {t("notFoundDetail", "The page you're looking for doesn't exist or may have been moved.")}
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <Button
            className="bg-gold-gradient text-gold-foreground hover:opacity-90"
            asChild
          >
            <Link to="/">{t("home", "Home")}</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/auctions">{t("searchAuctions", "Search auctions")}</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}
